import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {string} */
  #activeFinishName = '';

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.finishSelected, this.#handleFinishSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    queueMicrotask(() => {
      const finishName = this.#getSelectedFinishName();
      if (finishName) {
        this.#activeFinishName = finishName;
        this.#filterByFinish(finishName);
      }
    });
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeFinishName(value) {
    return value.trim().replace(/\s*-\s*Unavailable\s*$/i, '');
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {Event} event
   */
  #handleFinishSelected = (event) => {
    const finishName = this.#normalizeFinishName(
      /** @type {{ finishName?: string }} */ (event.detail)?.finishName ?? ''
    );
    if (!finishName) return;

    this.#activeFinishName = finishName;
    this.#filterByFinish(finishName);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @returns {string}
   */
  #getSelectedFinishName() {
    const persistedFinish = this.#normalizeFinishName(
      document.documentElement.dataset.activeFinishFilter ?? ''
    );
    if (persistedFinish) return persistedFinish;

    if (this.#activeFinishName) return this.#activeFinishName;

    const selectedItem = document.querySelector(
      'product-finishes-picker [data-finish-item][data-current-checked="true"]'
    );

    if (selectedItem instanceof HTMLElement) {
      const finishName = this.#normalizeFinishName(selectedItem.dataset.finishName ?? '');
      if (finishName) return finishName;
    }

    const finishesPicker = document.querySelector('product-finishes-picker');
    const optionName = finishesPicker?.dataset.standardVariantOptionName || 'Finishes';
    const finishSelect = document.querySelector(
      `variant-picker[data-template-product-match="true"] select.variant-option__select[data-finishes-dialog-option="${CSS.escape(optionName)}"]`
    );

    if (finishSelect instanceof HTMLSelectElement) {
      const finishName = this.#normalizeFinishName(finishSelect.value ?? '');
      if (finishName) return finishName;
    }

    return this.#normalizeFinishName(this.dataset.initialFinish ?? '');
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #handleize(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {string} normalizedAlt
   * @param {string} tag
   * @returns {string}
   */
  #getTagValue(normalizedAlt, tag) {
    const lower = normalizedAlt.toLowerCase();
    const needle = `#${tag}:`;
    const index = lower.indexOf(needle);
    if (index === -1) return '';

    let value = lower.slice(index + needle.length).split('#')[0].trim();

    // #pos / #bolt are numeric. Don't let trailing description stick on
    // (e.g. "#bolt:8 tf007 legend..." must read as "8", not the whole sentence).
    if (tag === 'pos' || tag === 'bolt') {
      const token = value.split(/\s+/)[0] ?? '';
      const digits = token.match(/^\d+/);
      return digits ? digits[0] : token;
    }

    return value;
  }

  /**
   * @param {string} normalizedAlt
   * @returns {string}
   */
  #getFinishFromAlt(normalizedAlt) {
    return this.#getTagValue(normalizedAlt, 'finish') || this.#getTagValue(normalizedAlt, 'color');
  }

  /**
   * @returns {string}
   */
  #getSelectedBoltLug() {
    if (this.dataset.filterByBolt !== 'true') return '';

    const optionName = this.dataset.boltOptionName || 'Bolt Pattern';
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) return this.dataset.initialBoltLug?.trim() ?? '';

    const boltSelect = variantPicker.querySelector(`select[name="options[${optionName}]"]`);
    if (boltSelect instanceof HTMLSelectElement && boltSelect.value) {
      const lug = boltSelect.value.toLowerCase().replace(/×/g, 'x').replace(/\s/g, '').split('x')[0]?.trim();
      if (lug) return lug;
    }

    const checkedBoltInput = Array.from(variantPicker.querySelectorAll('fieldset input:checked')).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      return input.name.toLowerCase().startsWith(`${optionName.toLowerCase()}-`);
    });

    if (checkedBoltInput instanceof HTMLInputElement && checkedBoltInput.value) {
      const lug = checkedBoltInput.value
        .toLowerCase()
        .replace(/×/g, 'x')
        .replace(/\s/g, '')
        .split('x')[0]
        ?.trim();
      if (lug) return lug;
    }

    return this.dataset.initialBoltLug?.trim() ?? '';
  }

  /**
   * @param {string} normalizedAlt
   * @returns {number}
   */
  #getPositionFromAlt(normalizedAlt) {
    const parsedPos = parseInt(this.#getTagValue(normalizedAlt, 'pos'), 10);
    return Number.isFinite(parsedPos) ? parsedPos : Number.MAX_SAFE_INTEGER;
  }

  /**
   * @param {HTMLElement} element
   * @returns {number}
   */
  #getMediaPosition(element) {
    const explicitPos = parseInt(element.dataset.mediaPos ?? '', 10);
    if (Number.isFinite(explicitPos)) return explicitPos;

    return this.#getPositionFromAlt(element.dataset.mediaAlt ?? '');
  }

  /**
   * @param {string} normalizedAlt
   * @param {string} finishName
   * @returns {boolean}
   */
  #mediaAltMatchesFinish(normalizedAlt, finishName) {
    const altFinish = this.#getFinishFromAlt(normalizedAlt);
    if (!altFinish) return false;

    const finishNormalized = finishName.toLowerCase().trim();
    const finishHandle = this.#handleize(finishName);
    if (!finishNormalized) return false;

    return altFinish === finishNormalized || this.#handleize(altFinish) === finishHandle;
  }

  /**
   * @param {string} normalizedAlt
   * @param {string} boltLug
   * @returns {boolean}
   */
  #mediaAltMatchesBolt(normalizedAlt, boltLug) {
    if (this.dataset.filterByBolt !== 'true') return true;

    const altBolt = this.#getTagValue(normalizedAlt, 'bolt');
    if (!altBolt) return true;
    if (!boltLug) return true;

    return altBolt === boltLug;
  }

  /**
   * @param {string} normalizedAlt
   * @param {string} finishName
   * @param {string} boltLug
   * @param {boolean} [requireBoltMatch=true]
   * @returns {boolean}
   */
  #mediaAltMatchesFilters(normalizedAlt, finishName, boltLug, requireBoltMatch = true) {
    if (!this.#mediaAltMatchesFinish(normalizedAlt, finishName)) return false;
    if (!requireBoltMatch) return true;

    return this.#mediaAltMatchesBolt(normalizedAlt, boltLug);
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {HTMLElement[]} thumbnails
   * @param {string} finishName
   * @param {string} boltLug
   * @param {boolean} requireBoltMatch
   * @returns {{ slide: HTMLElement; pos: number }[]}
   */
  #collectVisibleSlideEntries(slides, thumbnails, finishName, boltLug, requireBoltMatch) {
    /** @type {{ slide: HTMLElement; pos: number }[]} */
    const visibleEntries = [];

    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;

      const normalizedAlt = slide.dataset.mediaAlt ?? '';
      const matches = this.#mediaAltMatchesFilters(normalizedAlt, finishName, boltLug, requireBoltMatch);

      slide.hidden = !matches;

      const thumbnail = thumbnails[index];
      if (thumbnail instanceof HTMLElement) {
        thumbnail.hidden = !matches;
      }

      const thumbnailLists = this.slideshow?.querySelectorAll('.slideshow-controls__thumbnails') ?? [];
      const mediaId = slide.getAttribute('slide-id');
      if (mediaId) {
        thumbnailLists.forEach((list) => {
          if (!(list instanceof HTMLElement)) return;
          const matchedThumbnail = list.querySelector(
            `[data-media-id="${CSS.escape(String(mediaId))}"]`
          );
          if (matchedThumbnail instanceof HTMLElement) {
            matchedThumbnail.hidden = !matches;
          }
        });
      }

      if (matches) {
        visibleEntries.push({ slide, pos: this.#getMediaPosition(slide) });
      }
    });

    return visibleEntries;
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {string} finishName
   * @param {string} boltLug
   * @returns {boolean}
   */
  #filterAndReorderSlideshow(slides, finishName, boltLug) {
    const scroller = this.slideshow?.refs.scroller;
    const thumbnailLists = this.slideshow?.querySelectorAll('.slideshow-controls__thumbnails');

    if (!(scroller instanceof HTMLElement) || slides.length === 0) return false;

    /** @type {{ slide: HTMLElement; pos: number }[]} */
    let visibleEntries = this.#collectVisibleSlideEntries(slides, [], finishName, boltLug, true);

    if (visibleEntries.length === 0 && boltLug) {
      visibleEntries = this.#collectVisibleSlideEntries(slides, [], finishName, boltLug, false);
    }

    if (visibleEntries.length === 0) return false;

    visibleEntries.sort((a, b) => a.pos - b.pos);

    /** @type {HTMLElement[]} */
    const hiddenSlides = [];

    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      if (slide.hidden) hiddenSlides.push(slide);
    });

    /**
     * @param {HTMLElement} slide
     */
    const appendSlide = (slide) => {
      scroller.appendChild(slide);

      const mediaId = slide.getAttribute('slide-id');
      if (!mediaId || !thumbnailLists) return;

      thumbnailLists.forEach((list) => {
        if (!(list instanceof HTMLElement)) return;
        const thumbnail = list.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
        if (thumbnail instanceof HTMLElement) {
          list.appendChild(thumbnail);
        }
      });
    };

    for (const { slide } of visibleEntries) {
      appendSlide(slide);
    }

    for (const slide of hiddenSlides) {
      appendSlide(slide);
    }

    this.slideshow?.updatedCallback?.();

    return true;
  }

  /**
   * @param {string} finishName
   */
  #filterByFinish(finishName) {
    if (this.dataset.filterMediaByFinish !== 'true') return;

    finishName = this.#normalizeFinishName(finishName);
    if (!finishName) return;

    this.#activeFinishName = finishName;
    document.documentElement.dataset.activeFinishFilter = finishName;

    const boltLug = this.#getSelectedBoltLug();
    const slides = this.slideshow?.refs.slides ?? [];
    const gridItems = this.refs.media ?? [];

    const hasVisibleSlides = this.#filterAndReorderSlideshow(slides, finishName, boltLug);

    this.#filterAndReorderGrid(gridItems, finishName, boltLug);

    this.#filterZoomDialog(finishName, boltLug);

    if (!hasVisibleSlides || slides.length === 0) return;

    const firstSlide = this.slideshow?.slides?.[0];
    const slideId = firstSlide?.getAttribute('slide-id');

    if (slideId) {
      this.slideshow?.select({ id: slideId }, undefined, { animate: false });
    } else {
      this.slideshow?.select(0, undefined, { animate: false });
    }
  }

  /**
   * @param {string} finishName
   * @param {string} boltLug
   */
  #filterZoomDialog(finishName, boltLug) {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog) return;

    const mediaItems = zoomDialog.refs.media ?? [];
    const thumbnails = zoomDialog.refs.thumbnails?.children ?? [];
    if (mediaItems.length === 0) return;

    /**
     * @param {boolean} requireBoltMatch
     * @returns {number}
     */
    const applyFilter = (requireBoltMatch) => {
      let visibleCount = 0;

      mediaItems.forEach((mediaItem, index) => {
        if (!(mediaItem instanceof HTMLElement)) return;

        const normalizedAlt = mediaItem.dataset.mediaAlt ?? '';
        const matches = this.#mediaAltMatchesFilters(normalizedAlt, finishName, boltLug, requireBoltMatch);
        mediaItem.hidden = !matches;

        const thumbnail = thumbnails[index];
        if (thumbnail instanceof HTMLElement) {
          thumbnail.hidden = !matches;
          if (!matches) {
            thumbnail.setAttribute('aria-selected', 'false');
          }
        }

        if (matches) visibleCount += 1;
      });

      return visibleCount;
    };

    let visibleCount = applyFilter(true);

    if (visibleCount === 0 && boltLug) {
      visibleCount = applyFilter(false);
    }

    const thumbnailContainer = zoomDialog.querySelector('.dialog-thumbnails-list-container');
    if (thumbnailContainer instanceof HTMLElement) {
      thumbnailContainer.hidden = visibleCount <= 1;
    }

    if (visibleCount === 0) return;

    const firstVisibleIndex = mediaItems.findIndex(
      (item) => item instanceof HTMLElement && !item.hidden
    );

    if (firstVisibleIndex >= 0) {
      zoomDialog.selectThumbnail(firstVisibleIndex, { behavior: 'instant' });
    }
  }

  /**
   * @param {HTMLElement[]} gridItems
   * @param {string} finishName
   * @param {string} boltLug
   */
  #filterAndReorderGrid(gridItems, finishName, boltLug) {
    const grid = this.querySelector('.media-gallery__grid');
    if (!(grid instanceof HTMLElement) || gridItems.length === 0) return;

    /** @type {{ item: HTMLElement; pos: number }[]} */
    let visibleItems = [];
    /** @type {HTMLElement[]} */
    const hiddenItems = [];

    const collectMatches = (requireBoltMatch) => {
      visibleItems = [];
      hiddenItems.length = 0;

      gridItems.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;

        const normalizedAlt = item.dataset.mediaAlt ?? '';
        const matches = this.#mediaAltMatchesFilters(normalizedAlt, finishName, boltLug, requireBoltMatch);
        item.hidden = !matches;

        if (matches) {
          visibleItems.push({ item, pos: this.#getMediaPosition(item) });
        } else {
          hiddenItems.push(item);
        }
      });
    };

    collectMatches(true);

    if (visibleItems.length === 0 && boltLug) {
      collectMatches(false);
    }

    if (visibleItems.length === 0) {
      return;
    }

    visibleItems.sort((a, b) => a.pos - b.pos);

    for (const { item } of visibleItems) {
      grid.appendChild(item);
    }

    for (const item of hiddenItems) {
      grid.appendChild(item);
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
